import LoadingScreen from '../components/LoadingScreen';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useAppContext } from '../context/AppContext';

const STATUS_COLORS = { 'В процессе': '#FF9500', 'Выполнен': '#4CAF50', 'Отменён': '#FF3B30' };
const PAYMENT_METHODS = ['Наличные', 'Перевод', 'Расчётный счёт'];
const CITIES = ['Казань', 'Зеленодольск', 'Волжск'];
const DISTRICTS = ['Мирный', 'Город', 'Зеленый дол'];

const formatDeliveryDate = (day) => {
  if (!day) return '—';
  const parts = day.split('-');
  if (parts.length !== 3) return day;
  const date = new Date(day); date.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate()+1);
  const short = `${parts[2]}.${parts[1]}`;
  if (date.getTime() === today.getTime()) return `Сегодня · ${short}`;
  if (date.getTime() === tomorrow.getTime()) return `Завтра · ${short}`;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

export default function AdminOrderDetailsScreen() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user } = useAppContext();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);

  const [paymentVisible, setPaymentVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [cancelVisible, setCancelVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editVisible, setEditVisible] = useState(false);
  const [editBrand, setEditBrand] = useState('');
  const [editVolume, setEditVolume] = useState('');
  const [editQty, setEditQty] = useState('');
  const [editExchange, setEditExchange] = useState('');
  const [editPrice, setEditPrice] = useState(0);
  const [editCity, setEditCity] = useState(null);
  const [editDistrict, setEditDistrict] = useState(null);

  const load = useCallback(async () => {
    if (!orderId || !user?.isAdmin) { setLoading(false); return; }
    try {
      const [o, p] = await Promise.all([api.orders.get(orderId), api.products.list()]);
      setOrder(o);
      setProducts(p || []);
    } catch {} finally { setLoading(false); }
  }, [orderId, user]);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setEditBrand(order.brand || '');
    setEditVolume(order.volume || '');
    setEditQty(String(order.quantity ?? 1));
    setEditExchange(String(order.exchange_count ?? 0));
    setEditPrice(order.price ?? 0);
    setEditCity(order.city || null);
    setEditDistrict(order.district || null);
    setEditVisible(true);
  };

  const handleEdit = async () => {
    const qty = parseInt(editQty, 10);
    const exch = parseInt(editExchange, 10);
    if (!editBrand.trim()) { alert('Укажите марку воды'); return; }
    if (isNaN(qty) || qty < 1) { alert('Количество должно быть ≥ 1'); return; }
    if (isNaN(exch) || exch < 0) { alert('Обмен не может быть отрицательным'); return; }
    if (exch > qty) { alert('Обмен не может быть больше количества'); return; }
    setSaving(true);
    try {
      const updated = await api.orders.edit(orderId, { brand: editBrand.trim(), volume: editVolume.trim() || null, quantity: qty, exchange_count: exch, total_price: Math.max(0, editPrice * qty - exch * 280), city: editCity, district: editCity === 'Зеленодольск' ? editDistrict : null });
      setOrder(updated);
      setEditVisible(false);
    } catch { alert('Не удалось сохранить изменения'); }
    finally { setSaving(false); }
  };

  const handleTake = async () => {
    setSaving(true);
    try {
      const updated = await api.orders.take(orderId);
      setOrder(updated);
    } catch (e) {
      if (e.status === 409) { alert('Заказ уже взят другим курьером'); navigate(-1); }
      else alert('Не удалось взять заказ');
    } finally { setSaving(false); }
  };

  const handleDelivered = async () => {
    if (!selectedPayment) return;
    setSaving(true);
    try {
      await api.orders.deliver(orderId, selectedPayment);
      setPaymentVisible(false);
      alert('✅ Заказ доставлен!');
      navigate(-1);
    } catch { alert('Не удалось изменить статус'); }
    finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) return;
    setSaving(true);
    try {
      await api.orders.cancel(orderId, cancelReason.trim());
      setCancelVisible(false);
      navigate(-1);
    } catch { alert('Не удалось отменить заказ'); }
    finally { setSaving(false); }
  };

  const handleRelease = async () => {
    if (!window.confirm('Вернуть заказ в общий список?')) return;
    setSaving(true);
    try {
      await api.orders.release(orderId);
      navigate(-1);
    } catch { alert('Не удалось убрать заказ'); }
    finally { setSaving(false); }
  };

  const openMaps = () => {
    const addr = [order.city, order.district, order.address].filter(Boolean).join(', ');
    window.open(`https://yandex.ru/maps/?rtext=~${encodeURIComponent(addr)}&rtt=auto`, '_blank');
  };

  if (!user?.isAdmin) return <div style={centered}>Доступ только для администраторов</div>;
  if (loading) return <LoadingScreen />;
  if (!order) return <div style={centered}>Заказ не найден</div>;

  const statusColor = STATUS_COLORS[order.status] || '#FF9500';
  const statusLabel = order.status || 'В процессе';
  const total = order.total_price ?? order.price * order.quantity;
  const isActive = !order.status || order.status === 'В процессе';
  const isAvailable = isActive && !order.courier_id;
  const isMine = isActive && String(order.courier_id) === String(user?.id);
  const isTakenByOther = isActive && order.courier_id && String(order.courier_id) !== String(user?.id);

  const editTotal = Math.max(0, editPrice * (parseInt(editQty, 10) || 0) - (parseInt(editExchange, 10) || 0) * 280);

  return (
    <div style={{ paddingBottom: 100 }}>
      {/* Шапка */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--card)', gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', color: 'var(--primary)', fontSize: 22, fontWeight: 600, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Заказ #{order.id}</div>
          <span style={{ fontSize: 12, fontWeight: 700, color: statusColor, background: statusColor + '22', padding: '2px 8px', borderRadius: 8 }}>{statusLabel}</span>
        </div>
        <div style={{ width: 40 }} />
      </div>

      <div style={{ padding: '0 16px' }}>
        {/* Товар */}
        <p style={blockLabel}>Товар</p>
        <div style={blockStyle}>
          {order.brand && <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--primary)', marginBottom: 12 }}>{order.brand}{order.volume ? ` ${order.volume}` : ''}</div>}
          <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
            {[['Бутылей', order.quantity], ['На обмен', order.exchange_count ?? 0], ['Сумма', `${total} ₽`]].map(([label, val], i) => (
              <div key={label} style={{ flex: 1, textAlign: 'center', padding: '10px 4px', borderLeft: i > 0 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: label === 'Сумма' ? '#4CAF50' : 'var(--text)' }}>{val}</div>
              </div>
            ))}
          </div>
          {order.payment_method && <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-light)' }}>Оплата: <strong>{order.payment_method}</strong></div>}
        </div>

        {/* Доставка */}
        <p style={blockLabel}>Доставка</p>
        <div style={blockStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{formatDeliveryDate(order.delivery_day)}</span>
            {order.city && <span style={{ fontSize: 14, color: 'var(--primary)', fontWeight: 600 }}>{order.city}{order.district ? ` · ${order.district}` : ''}</span>}
          </div>
          {order.address && <div style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5 }}>{order.address}</div>}
        </div>

        {/* Клиент */}
        <p style={blockLabel}>Клиент</p>
        <div style={{ ...blockStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            {order.user_name && <div style={{ fontWeight: 700, marginBottom: 2 }}>{order.user_name}</div>}
            <div style={{ fontSize: 16 }}>{order.phone || '—'}</div>
          </div>
          {order.phone && (
            <a href={`tel:${order.phone}`} style={{ background: 'var(--primary)', color: '#fff', padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Позвонить
            </a>
          )}
        </div>

        {/* Комментарий */}
        {order.comment && (
          <>
            <p style={blockLabel}>Комментарий</p>
            <div style={blockStyle}><p style={{ color: '#E53935', fontSize: 14, lineHeight: 1.5 }}>{order.comment}</p></div>
          </>
        )}

        {/* Причина отмены */}
        {order.cancel_reason && (
          <>
            <p style={blockLabel}>Причина отмены</p>
            <div style={{ ...blockStyle, borderLeft: '3px solid #FF3B30' }}><p style={{ color: '#FF3B30', fontSize: 14 }}>{order.cancel_reason}</p></div>
          </>
        )}

        {/* Действия */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {isActive && <button onClick={openEdit} disabled={saving} style={{ ...actionBtn, background: '#007AFF' }}>Редактировать заказ</button>}
          <button onClick={openMaps} disabled={saving} style={{ ...actionBtn, background: 'none', color: '#007AFF', border: '1.5px solid #007AFF' }}>Проложить маршрут</button>
          {isAvailable && <button onClick={handleTake} disabled={saving} style={{ ...actionBtn, background: 'var(--primary)' }}>{saving ? '...' : 'Взять в работу'}</button>}
          {isMine && (
            <>
              <button onClick={() => setPaymentVisible(true)} disabled={saving} style={{ ...actionBtn, background: '#4CAF50' }}>Доставлен</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setCancelVisible(true)} disabled={saving} style={{ ...actionBtn, flex: 1, background: '#FF3B30' }}>Отменить</button>
                <button onClick={handleRelease} disabled={saving} style={{ ...actionBtn, flex: 1, background: 'none', color: '#FF9500', border: '1.5px solid #FF9500' }}>Убрать из работы</button>
              </div>
            </>
          )}
          {isTakenByOther && <div style={{ background: 'var(--input-bg)', borderRadius: 12, padding: 14, textAlign: 'center', color: 'var(--text-light)', fontWeight: 600 }}>Заказ взят другим курьером</div>}
        </div>
      </div>

      {/* Модал редактирования */}
      {editVisible && (
        <Modal onClose={() => setEditVisible(false)} title="Редактировать заказ" subtitle="Изменить состав и количество">
          <p style={mLabel}>Вода (марка)</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {products.map(p => (
              <button key={p.id} onClick={() => { setEditBrand(p.brand); setEditVolume(p.volume || ''); setEditPrice(p.price ?? 0); }}
                style={{ ...chipStyle, borderColor: editBrand === p.brand && editVolume === p.volume ? '#007AFF' : 'var(--border)', background: editBrand === p.brand && editVolume === p.volume ? '#007AFF15' : 'var(--bg)', color: editBrand === p.brand && editVolume === p.volume ? '#007AFF' : 'var(--text)' }}>
                {p.brand}{p.volume ? ` ${p.volume}` : ''}
              </button>
            ))}
          </div>
          <p style={mLabel}>Город</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {CITIES.map(c => <button key={c} onClick={() => { setEditCity(c); setEditDistrict(null); }} style={{ ...chipStyle, borderColor: editCity === c ? '#007AFF' : 'var(--border)', background: editCity === c ? '#007AFF15' : 'var(--bg)', color: editCity === c ? '#007AFF' : 'var(--text)' }}>{c}</button>)}
          </div>
          {editCity === 'Зеленодольск' && (
            <>
              <p style={mLabel}>Район</p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {DISTRICTS.map(d => <button key={d} onClick={() => setEditDistrict(d)} style={{ ...chipStyle, borderColor: editDistrict === d ? '#007AFF' : 'var(--border)', background: editDistrict === d ? '#007AFF15' : 'var(--bg)', color: editDistrict === d ? '#007AFF' : 'var(--text)' }}>{d}</button>)}
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><p style={mLabel}>Бутылей</p><input type="number" min={1} value={editQty} onChange={e => setEditQty(e.target.value)} style={mInput} /></div>
            <div style={{ flex: 1 }}><p style={mLabel}>На обмен</p><input type="number" min={0} value={editExchange} onChange={e => setEditExchange(e.target.value)} style={mInput} /></div>
          </div>
          <div style={{ marginTop: 12, padding: 10, background: 'var(--bg)', borderRadius: 10 }}>
            <span style={{ fontWeight: 700, color: '#4CAF50', fontSize: 15 }}>Итого: {editTotal} ₽</span>
          </div>
          <button onClick={handleEdit} disabled={saving} style={{ ...actionBtn, background: '#4CAF50', marginTop: 12 }}>Сохранить</button>
          <button onClick={() => setEditVisible(false)} style={{ width: '100%', padding: 10, background: 'none', color: 'var(--text-light)', fontWeight: 600, marginTop: 8, cursor: 'pointer' }}>Назад</button>
        </Modal>
      )}

      {/* Модал оплаты */}
      {paymentVisible && (
        <Modal onClose={() => { setPaymentVisible(false); setSelectedPayment(null); }} title="Способ оплаты" subtitle="Выберите способ перед подтверждением доставки">
          {PAYMENT_METHODS.map(m => (
            <div key={m} onClick={() => setSelectedPayment(m)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderRadius: 12, border: '1.5px solid', borderColor: selectedPayment === m ? '#4CAF50' : 'var(--border)', background: selectedPayment === m ? '#4CAF5015' : 'var(--bg)', marginBottom: 8, cursor: 'pointer' }}>
              <span style={{ fontWeight: selectedPayment === m ? 700 : 500, color: selectedPayment === m ? '#4CAF50' : 'var(--text)' }}>{m}</span>
              {selectedPayment === m && <span style={{ color: '#4CAF50', fontWeight: 700 }}>✓</span>}
            </div>
          ))}
          <button onClick={handleDelivered} disabled={!selectedPayment || saving} style={{ ...actionBtn, background: '#4CAF50', marginTop: 12, opacity: (!selectedPayment || saving) ? 0.5 : 1 }}>Подтвердить доставку</button>
          <button onClick={() => { setPaymentVisible(false); setSelectedPayment(null); }} style={{ width: '100%', padding: 10, background: 'none', color: 'var(--text-light)', fontWeight: 600, marginTop: 8, cursor: 'pointer' }}>Назад</button>
        </Modal>
      )}

      {/* Модал отмены */}
      {cancelVisible && (
        <Modal onClose={() => { setCancelVisible(false); setCancelReason(''); }} title="Причина отмены" subtitle="Обязательно укажите причину">
          <textarea placeholder="Например: клиент не открыл дверь..." value={cancelReason} onChange={e => setCancelReason(e.target.value)} style={{ ...mInput, minHeight: 80, resize: 'vertical', width: '100%' }} />
          <button onClick={handleCancel} disabled={!cancelReason.trim() || saving} style={{ ...actionBtn, background: '#FF3B30', marginTop: 12, opacity: (!cancelReason.trim() || saving) ? 0.5 : 1 }}>Отменить заказ</button>
          <button onClick={() => { setCancelVisible(false); setCancelReason(''); }} style={{ width: '100%', padding: 10, background: 'none', color: 'var(--text-light)', fontWeight: 600, marginTop: 8, cursor: 'pointer' }}>Назад</button>
        </Modal>
      )}
    </div>
  );
}

function Modal({ onClose, title, subtitle, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--card)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{title}</h3>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 16 }}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

const centered = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh', fontSize: 16, color: 'var(--text-light)' };
const blockLabel = { fontSize: 12, fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 16, marginBottom: 6 };
const blockStyle = { background: 'var(--card)', borderRadius: 14, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const actionBtn = { display: 'block', width: '100%', padding: 14, borderRadius: 12, color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', border: 'none', textAlign: 'center' };
const mLabel = { fontSize: 12, fontWeight: 700, color: 'var(--text-light)', marginBottom: 6, marginTop: 8 };
const mInput = { display: 'block', width: '100%', background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 15, color: 'var(--text)', outline: 'none' };
const chipStyle = { padding: '6px 14px', borderRadius: 20, border: '1.5px solid', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
